import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Lock } from "lucide-react";
import { track, identifyUser, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Collects buyer's name+email, then redirects to Stripe Checkout.
 * The amount is never sent from the frontend — it's set server-side from PRODUCTS.
 */
export default function BuyModal({ open, onOpenChange, product }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  if (!product) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        product_id: product.id,
        origin_url: window.location.origin,
        first_name: firstName.trim(),
        email: email.trim(),
      });
      identifyUser(email, { first_name: firstName.trim(), source: "checkout" });
      track(EVENTS.CHECKOUT_INITIATED, {
        product_id: product.id,
        product_title: product.title,
        price_label: product.price_label,
      });
      if (res.data?.url) {
        track(EVENTS.CHECKOUT_REDIRECTED, {
          product_id: product.id,
          session_id: res.data.session_id || null,
        });
        window.location.href = res.data.url;
        return;
      }
      throw new Error("No checkout URL received");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setFirstName(""); setEmail(""); setLoading(false); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md rounded-3xl bg-white border-[#EDE9E1]" data-testid="buy-modal">
        <DialogHeader>
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-2">
            {product.price_label} · Instant download
          </div>
          <DialogTitle className="font-display font-bold text-2xl text-[#333333]">{product.title}</DialogTitle>
          <DialogDescription className="text-[#4A4A4A] pt-1">
            You&apos;ll be redirected to Stripe to complete payment. Your PDF is delivered to your email and available for download immediately after.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-2" data-testid="buy-form">
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="h-12 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0"
            data-testid="buy-input-first-name"
            autoComplete="given-name"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="h-12 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0"
            data-testid="buy-input-email"
            autoComplete="email"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#333333] hover:bg-black text-white font-semibold disabled:opacity-70"
            data-testid="buy-submit"
          >
            {loading ? "Redirecting to Stripe…" : `Continue to Stripe — ${product.price_label}`}
            {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
          <p className="text-xs text-[#8A8A8A] pt-1 flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Secure payment by Stripe. We never see your card details.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
